import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UPButton from 'components/button';
import UPInput from 'components/input';
import {
  MFA_CHALLENGE__BACKUP_CODE_INSTRUCTIONS,
  MFA_CHALLENGE__BACKUP_CODE_LABEL,
  MFA_CHALLENGE__BACKUP_CODE_TITLE,
  MFA_CHALLENGE__BACK_TO_SIGN_IN,
  MFA_CHALLENGE__RETRY_IN_SECONDS,
  MFA_CHALLENGE__USE_AUTHENTICATOR,
  MFA_CHALLENGE__VERIFY,
} from 'translations/constants';
import { MfaFailure, isCompleteBackupCode, normaliseBackupCode, translateMfaFailure } from '../mfa-helpers';
import { MfaButtonRow, MfaCardTitle, MfaErrorSlot, MfaInstructions } from '../mfa-styles';
import { MfaBackupCodeAction } from './styles';

interface MfaBackupCodeViewProps {
  submitting: boolean;
  failure: MfaFailure;
  retrySeconds: number;
  /** Resolves true when the code was accepted. False clears the field so the user can try another. */
  onSubmit: (code: string) => Promise<boolean>;
  onBackToLogin: () => void;
  /** Swaps this view back for the authenticator one. Same challenge, same token. */
  onUseAuthenticator: () => void;
  onCodeEdited: () => void;
}

const ENTER_KEY_CODE = 13;

/**
 * The recovery view: one of the eight single-use codes issued at enrollment, for a user who cannot
 * reach their authenticator app.
 *
 * It answers the *same* challenge as `MfaTotpView` with the same mfaToken, so the 5-minute clock and
 * the backoff carry straight over from whatever the user already tried — see `useMfaChallenge`.
 */
const MfaBackupCodeView = (props: MfaBackupCodeViewProps) => {
  const { submitting, failure, retrySeconds, onSubmit, onBackToLogin, onUseAuthenticator, onCodeEdited } = props;
  const [t] = useTranslation();
  const [code, setCode] = useState('');

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCode(normaliseBackupCode(event.target.value));
      onCodeEdited();
    },
    [onCodeEdited],
  );

  const waiting = retrySeconds > 0;
  const canSubmit = !submitting && !waiting && isCompleteBackupCode(code);

  // Clearing on rejection is deliberate, and the copy has to stay vague to match: a spent code is
  // simply absent from the set, so the server cannot tell a reused code from a wrong guess and we
  // must not write copy claiming it can. Emptying the field is the honest nudge to try another one.
  const submitCode = useCallback(async () => {
    const accepted = await onSubmit(code);
    if (!accepted) {
      setCode('');
    }
  }, [onSubmit, code]);

  const onKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      const keyCode = event.keyCode || event.which;
      if (keyCode === ENTER_KEY_CODE && canSubmit) {
        submitCode();
      }
    },
    [canSubmit, submitCode],
  );

  return (
    <>
      <MfaCardTitle>{t(MFA_CHALLENGE__BACKUP_CODE_TITLE)}</MfaCardTitle>
      <MfaInstructions>{t(MFA_CHALLENGE__BACKUP_CODE_INSTRUCTIONS)}</MfaInstructions>
      {/* Accepts the code however it was transcribed — lowercase, spaced, or with the hyphen left
          out — because normaliseBackupCode mirrors exactly what the server does before matching.
          No maxLength here either, for the same reason as the six-digit field: the DOM would apply
          it to the raw string and eat a character from any paste containing a separator. */}
      <UPInput
        value={code}
        onChange={handleCodeChange}
        onKeyPress={onKeyPress}
        label={t(MFA_CHALLENGE__BACKUP_CODE_LABEL)}
        placeholder={t(MFA_CHALLENGE__BACKUP_CODE_LABEL)}
        error={!!failure}
        inputMode="text"
        autoComplete="off"
      />
      <MfaErrorSlot>{failure && translateMfaFailure(t, failure)}</MfaErrorSlot>
      <MfaButtonRow>
        <UPButton text={t(MFA_CHALLENGE__BACK_TO_SIGN_IN)} onClick={onBackToLogin} outlined />
        <UPButton
          text={waiting ? t(MFA_CHALLENGE__RETRY_IN_SECONDS, { seconds: retrySeconds }) : t(MFA_CHALLENGE__VERIFY)}
          onClick={submitCode}
          disabled={!canSubmit}
        />
      </MfaButtonRow>
      <MfaBackupCodeAction type="button" onClick={onUseAuthenticator}>
        {t(MFA_CHALLENGE__USE_AUTHENTICATOR)}
      </MfaBackupCodeAction>
    </>
  );
};

export default MfaBackupCodeView;
